import { isEmpty, isNumber } from "lodash";
import { useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";

import { CalendlyStatus, useGetUserInfo } from "@Roo/api/user/useGetUserInfo";
import { License } from "@Roo/api/vet";
import {
  HC_TOPICS_TO_DISCUSS_OPTIONS,
  LC_TOPICS_TO_DISCUSS_OPTIONS,
  ReasonForRegistering,
} from "@Roo/constants/checkBoxConstants";
import { type UserType } from "@Roo/constants/UserTypeConstants";

import { isTechLicenseValid, isVetLicenseValid } from "../../RegistrationLicense/service";
import { useRegistrationContext } from "../registrationContext";
import { RegistrationStep, getIsEntity } from "../service";

import { useRedirect } from "./useRedirect";

export type RegistrationProgress = RegistrationStep | "HighCommitment" | "Loading" | "Error";

export const useRegistrationProgress = (): RegistrationProgress => {
  const { registrationContext, setRegistrationContext } = useRegistrationContext();
  const { redirectToVetHomepage } = useRedirect();

  const [userId] = useLocalStorage<number>("userId", registrationContext.userId, {
    serializer: (v) => v.toString(),
    deserializer: (v) => parseInt(v),
  });

  const [userType] = useLocalStorage<number>("userTypeId", registrationContext.userType ?? 0, {
    serializer: (v) => v.toString(),
    deserializer: (v) => parseInt(v),
  });

  const [email] = useLocalStorage<string>("email", registrationContext.email, {
    serializer: (v) => v,
    deserializer: (v) => v,
  });

  const {
    isLoading,
    isError,
    isFetching,
    isSuccess,
    data: userInfo,
  } = useGetUserInfo({ userTypeId: userType, email });

  useEffect(() => {
    if (isSuccess && userInfo?.data.success) {
      const { firstName, lastName, zipcode, licenses, utmSource, reasonForRegistering } =
        userInfo.data.results[0];

      const highCommitment = reasonForRegistering === ReasonForRegistering.FindingTheRightShift;

      const topicsToDiscuss = highCommitment
        ? HC_TOPICS_TO_DISCUSS_OPTIONS
        : LC_TOPICS_TO_DISCUSS_OPTIONS;

      setRegistrationContext({
        registrationContext: {
          ...registrationContext,
          firstName: firstName ?? "",
          lastName: lastName ?? "",
          zipcode: zipcode ?? "",
          // @ts-expect-error TS2322
          licenses: licenses ?? [],
          utmSource: utmSource ?? "",
          reasonForRegistering: reasonForRegistering ?? "",
          topicsToDiscuss: topicsToDiscuss.map((option) => ({
            ...option,
            checked: false,
          })),
        },
      });
    }
  }, [isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isError) {
    return "Error";
  }

  if (isFetching && isLoading) {
    return "Loading";
  }

  // Landing
  const firstName = registrationContext.firstName || userInfo?.data.results[0].firstName;
  const lastName = registrationContext.lastName || userInfo?.data.results[0].lastName;
  const isLandingDone = isNumber(userType) && !isEmpty(firstName) && !isEmpty(lastName);

  // SignUp
  const isSignUpDone = userId !== -1;

  // Location
  const zipcode = registrationContext.zipcode || userInfo?.data.results[0].zipcode;
  const isLocationDone = !isEmpty(zipcode) && isNumber(registrationContext.travelDistance);

  // License
  const { isVet } = getIsEntity(userType as UserType);

  const licenses = registrationContext.licenses?.find((license: License) => !!license.id)
    ? registrationContext.licenses
    : userInfo?.data.results[0].licenses || [];

  const isLicenseDone = isVet
    ? // @ts-expect-error TS2322
      isVetLicenseValid(licenses)
    : isTechLicenseValid({
        // @ts-expect-error TS2322
        licenses,
        // @TODO:
        // We don't correctly populate isCertified, isRegistered, isLicensed, or isVTS
        // in the registration context when signing in, which means that you will
        // always have to re-check the appropriate boxes if you sign out and then
        // sign back in to continue registration. This isn't a critical bug, but
        // it's worth mentioning. This only impacts the tech flow; the vet flow
        // works as expected.
        isCertified: registrationContext.isCertified,
        isRegistered: registrationContext.isRegistered,
        isLicensed: registrationContext.isLicensed,
        isVTS: registrationContext.isVTS,
        // @ts-expect-error TS2322
        techRegistrationFlow: registrationContext.techRegistrationFlow,
      });

  // Education
  const isEducationDone =
    isVet ||
    (!isEmpty(registrationContext.school) && isNumber(registrationContext.yearsOfPractice));

  // Vet flow
  // Landing-> Signup-> Location-> License-> Goals-> Preferences-> Calendly-> Referral

  if (!isLandingDone) {
    return "Landing";
  } else if (!isSignUpDone) {
    return "SignUp";
  } else if (!isLocationDone) {
    return "Location";
  } else if (!isLicenseDone) {
    return "License";
  } else if (!isEducationDone) {
    return "Education";
  }

  if (isVet && !!userInfo?.data.results[0].isRegistrationComplete) {
    redirectToVetHomepage();
    return "Loading";
  }

  // Roo Goals is applicable only for vets
  const reasonForRegistering =
    registrationContext?.reasonForRegistering || userInfo?.data.results[0].reasonForRegistering;
  const isDoneWithRooGoals = isVet ? !!reasonForRegistering : true;
  if (!isDoneWithRooGoals) {
    return "WhyAreYouOnRoo";
  }
  const highCommitment = reasonForRegistering === ReasonForRegistering.FindingTheRightShift;

  // Vet Preferences is applicable only for vets
  const yearsOfPractice =
    registrationContext?.yearsOfPractice || userInfo?.data.results[0].yearsOfPractice;
  const isVetPreferencesDone = isVet ? isNumber(yearsOfPractice) : true;
  if (!isVetPreferencesDone) {
    // Make sure they receive the right questions depending on reason for registering
    return highCommitment ? "HighCommitment" : "VetPreferences";
  }

  if (isVet) {
    const showRooRepStep = userInfo?.data.results[0].calendlyStatus !== CalendlyStatus.Scheduled;
    return showRooRepStep ? "MeetRooRep" : "Referral";
  }

  return "Hireflix";
};
